import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Segment, Loader, Message, Table, Icon, Header, Input, Button, Label } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../app/stores/store';
import { BookingPriceCalculation } from '../../app/models/Calendar';

interface Props {
  assetId: string;
  dateTimeStart: Date;
  dateTimeEnd: Date;
  capacityUnitsRequested: number;
  autoCalculate?: boolean;
  bookingId?: string;
  manualPriceOverride?: number | null;
  onManualPriceOverrideChange: (value: number | null) => void;
}

const DEBOUNCE_MS = 600;

const PriceCalculator: React.FC<Props> = observer(({
  assetId,
  dateTimeStart,
  dateTimeEnd,
  capacityUnitsRequested,
  autoCalculate = true,
  bookingId,
  manualPriceOverride,
  onManualPriceOverrideChange,
}) => {
  const { t } = useTranslation();
  const { calendarStore } = useStore();
  const [calculating, setCalculating] = useState(false);

  const [inputValue, setInputValue] = useState<string>(
    manualPriceOverride != null ? String(manualPriceOverride) : ''
  );
  const [overrideError, setOverrideError] = useState<string | null>(null);

  useEffect(() => {
    if (manualPriceOverride == null) {
      setInputValue('');
      setOverrideError(null);
    } else {
      const alreadyMatches =
        parseFloat(inputValue) !== 0 &&
        Math.round(parseFloat(inputValue) * 100) === Math.round(manualPriceOverride * 100);
      if (!alreadyMatches) {
        setInputValue(String(manualPriceOverride));
        setOverrideError(null);
      }
    }
  }, [manualPriceOverride]);

  const storeRef = useRef(calendarStore);
  useEffect(() => { storeRef.current = calendarStore; }, [calendarStore]);

  useEffect(() => {
    if (!autoCalculate) return;
    if (!assetId || !dateTimeStart || !dateTimeEnd || capacityUnitsRequested <= 0) return;

    const timer = setTimeout(async () => {
      setCalculating(true);
      const calculation: BookingPriceCalculation = {
        assetId,
        dateTimeStart,
        dateTimeEnd,
        capacityUnitsRequested,
        bookingId,
      };
      await storeRef.current.calculateBookingPrice(calculation);
      setCalculating(false);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, dateTimeStart, dateTimeEnd, capacityUnitsRequested, autoCalculate, bookingId]);

  const handleInputChange = useCallback((rawValue: string) => {
    setInputValue(rawValue);
    if (rawValue.trim() === '') {
      setOverrideError(null);
      onManualPriceOverrideChange(null);
      return;
    }
    const parsed = parseFloat(rawValue);
    if (isNaN(parsed) || parsed <= 0) {
      setOverrideError(t('pricing.overrideError'));
      return;
    }
    setOverrideError(null);
    onManualPriceOverrideChange(Math.round(parsed * 100) / 100);
  }, [onManualPriceOverrideChange, t]);

  const handleBlur = useCallback(() => {
    const parsed = parseFloat(inputValue);
    if (!isNaN(parsed) && parsed > 0) {
      setInputValue((Math.round(parsed * 100) / 100).toFixed(2));
    }
  }, [inputValue]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
    if (e.key === 'Escape') {
      setInputValue(manualPriceOverride != null ? String(manualPriceOverride) : '');
      setOverrideError(null);
    }
  }, [manualPriceOverride]);

  const clearOverride = useCallback(() => {
    setInputValue('');
    setOverrideError(null);
    onManualPriceOverrideChange(null);
  }, [onManualPriceOverrideChange]);

  if (calculating) {
    return (
      <Segment>
        <Loader active inline="centered" size="small">
          {t('pricing.calculating')}
        </Loader>
      </Segment>
    );
  }

  const priceResult = calendarStore.currentPriceCalculation;
  if (!priceResult) return null;

  // ── CAPACITY UNAVAILABLE ──────────────────────────────────────────────────
  // The pricing service returns isCapacityAvailable=false instead of throwing
  // when a slot is at full capacity. This check only covers the FIRST preview
  // occurrence date (the one the PriceCalculator was asked to price). There
  // may be additional conflicting dates across the full recurring series —
  // those are discovered and listed exhaustively when the user attempts to
  // create the series (the backend checks all occurrences and returns the
  // full conflict list in skippedSlots, shown in ResultPanel on step 2).
  if (priceResult.isCapacityAvailable === false) {
    const conflictDate = dateTimeStart.toLocaleDateString(undefined, {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
    const conflictTimeStart = dateTimeStart.toLocaleTimeString(undefined, {
      hour: '2-digit', minute: '2-digit',
    });
    const conflictTimeEnd = dateTimeEnd.toLocaleTimeString(undefined, {
      hour: '2-digit', minute: '2-digit',
    });

    return (
      <Segment color="red">
        <Header as="h4" style={{ marginTop: 0, color: '#db2828' }}>
          <Icon name="exclamation triangle" color="red" />
          {t('pricing.capacityUnavailableTitle')}
        </Header>
        <Message negative size="small" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
          <Message.Content>
            <p style={{ marginBottom: '0.5rem' }}>
              <Icon name="ban" />
              {t('pricing.capacityUnavailableBody')}
            </p>
            <div style={{
              background: 'rgba(0,0,0,0.06)',
              borderRadius: '4px',
              padding: '0.5rem 0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}>
              <Icon name="calendar times outline" style={{ margin: 0 }} />
              <strong>{conflictDate}</strong>
              <span style={{ color: '#9a2020' }}>
                {conflictTimeStart} – {conflictTimeEnd}
              </span>
            </div>
          </Message.Content>
        </Message>
        <Message warning size="small" style={{ marginTop: 0 }}>
          <Icon name="info circle" />
          {t('pricing.capacityUnavailableMoreConflicts')}
        </Message>
      </Segment>
    );
  }

  // ── NORMAL PRICE BREAKDOWN ────────────────────────────────────────────────

  const durationHours = (dateTimeEnd.getTime() - dateTimeStart.getTime()) / (1000 * 60 * 60);
  const effectivePrice = manualPriceOverride != null ? manualPriceOverride : priceResult.finalPrice;
  const hasOverride = manualPriceOverride != null;

  return (
    <Segment color={hasOverride ? 'orange' : 'blue'}>
      <Header as="h4" style={{ marginTop: 0 }}>
        <Icon name="calculator" />
        {t('pricing.priceBreakdown')}
        {hasOverride && (
          <Label color="orange" size="small" style={{ marginLeft: '8px', verticalAlign: 'middle' }}>
            <Icon name="exclamation" />
            {t('pricing.manualOverrideActive')}
          </Label>
        )}
      </Header>

      <Table basic="very" compact>
        <Table.Body>
          <Table.Row>
            <Table.Cell><strong>{t('pricing.capacity')}:</strong></Table.Cell>
            <Table.Cell textAlign="right">{capacityUnitsRequested} {t('capacity.units')}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell><strong>{t('pricing.duration')}:</strong></Table.Cell>
            <Table.Cell textAlign="right">{durationHours.toFixed(1)} {t('pricing.hours')}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell><strong>{t('pricing.basePrice')}:</strong></Table.Cell>
            <Table.Cell textAlign="right">${priceResult.basePrice.toFixed(2)}</Table.Cell>
          </Table.Row>

          {priceResult.appliedDiscounts && priceResult.appliedDiscounts.length > 0 && (
            <>
              <Table.Row>
                <Table.Cell colSpan={2}>
                  <Header as="h5" style={{ margin: '10px 0 5px 0' }}>{t('pricing.appliedDiscounts')}:</Header>
                </Table.Cell>
              </Table.Row>
              {priceResult.appliedDiscounts.map((discount, index) => (
                <Table.Row key={index}>
                  <Table.Cell style={{ paddingLeft: '20px', color: '#21ba45' }}>
                    <Icon name="tag" color="green" />{discount.description}
                  </Table.Cell>
                  <Table.Cell textAlign="right" style={{ color: '#21ba45' }}>
                    -${discount.amount.toFixed(2)}
                  </Table.Cell>
                </Table.Row>
              ))}
              <Table.Row>
                <Table.Cell><strong>{t('pricing.totalDiscount')}:</strong></Table.Cell>
                <Table.Cell textAlign="right" style={{ color: '#21ba45' }}>
                  <strong>-${priceResult.discountAmount.toFixed(2)}</strong>
                </Table.Cell>
              </Table.Row>
            </>
          )}

          <Table.Row style={{ borderTop: '2px solid #ddd' }}>
            <Table.Cell>
              <span style={{ color: hasOverride ? '#999' : undefined }}>
                <strong>{hasOverride ? t('pricing.calculatedPrice') : t('pricing.finalPrice')}:</strong>
              </span>
            </Table.Cell>
            <Table.Cell textAlign="right">
              <span style={{
                color: hasOverride ? '#999' : undefined,
                textDecoration: hasOverride ? 'line-through' : undefined,
                fontSize: hasOverride ? '0.9em' : undefined,
              }}>
                ${priceResult.finalPrice.toFixed(2)}
              </span>
            </Table.Cell>
          </Table.Row>

          <Table.Row>
            <Table.Cell>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Icon name="edit outline" color="orange" />
                <strong style={{ color: '#e67e22' }}>{t('pricing.manualOverride')}:</strong>
              </div>
              <div style={{ fontSize: '0.78em', color: '#888', marginTop: '2px' }}>
                {t('pricing.manualOverrideHint')}
              </div>
            </Table.Cell>
            <Table.Cell textAlign="right">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
                <Input
                  placeholder={t('pricing.manualOverridePlaceholder')}
                  value={inputValue}
                  onChange={(_, { value }) => handleInputChange(value)}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  size="small"
                  type="number"
                  min="0.01"
                  step="0.01"
                  icon="dollar sign"
                  iconPosition="left"
                  error={!!overrideError}
                  style={{ width: '130px' }}
                />
                {hasOverride && (
                  <Button icon="times" size="mini" basic color="grey"
                    title={t('pricing.clearOverride')} onClick={clearOverride}
                    style={{ padding: '6px 8px' }} />
                )}
              </div>
              {overrideError && (
                <div style={{ color: '#db2828', fontSize: '0.78em', marginTop: '4px', textAlign: 'right' }}>
                  {overrideError}
                </div>
              )}
            </Table.Cell>
          </Table.Row>

          <Table.Row style={{ borderTop: hasOverride ? '2px solid #e67e22' : 'none' }}>
            <Table.Cell>
              <Header as="h4" style={{ margin: 0, color: hasOverride ? '#e67e22' : undefined }}>
                {t('pricing.finalPrice')}:
              </Header>
            </Table.Cell>
            <Table.Cell textAlign="right">
              <Header as="h3" color={hasOverride ? undefined : 'blue'}
                style={{ margin: 0, color: hasOverride ? '#e67e22' : undefined }}>
                ${effectivePrice.toFixed(2)}
              </Header>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>

      {priceResult.discountAmount > 0 && !hasOverride && (
        <Message positive size="small" style={{ marginTop: '10px' }}>
          <Icon name="check circle" />
          {t('pricing.youSave', { amount: priceResult.discountAmount.toFixed(2) })}
        </Message>
      )}

      {hasOverride && (
        <Message warning size="small" style={{ marginTop: '10px' }}>
          <Icon name="info circle" />
          {t('pricing.overrideNotice', {
            calculated: priceResult.finalPrice.toFixed(2),
            override: effectivePrice.toFixed(2),
          })}
        </Message>
      )}
    </Segment>
  );
});

export default PriceCalculator;